import { deleteCookie, getCookie, getCookies, setCookie } from "cookies-next";
import { getRandom } from "./varities";
import { CookieKeys } from "@/constants/cookiesEnums";
import { browserCookieAttrs } from "./storefront-cookie-options";

const storeCookei = (key: string, value: string, age: number = 30) => {
  setCookie(key, value, {
    path: "/",
    maxAge: 60 * 60 * 24 * age,
    ...browserCookieAttrs(),
  });
};

// Same attrs as the write, or the delete is rejected inside a cross-site frame.
const removeCookie = (key: string) =>
  deleteCookie(key, { path: "/", ...browserCookieAttrs() });

// ─── Client-side memoized cookie reads ──────────────────────────────────────
// `cookies-next` getCookie re-parses the ENTIRE cookie jar on every call. The lang / branch /
// currency reads below feed `getKey()`, which runs in render across ~100 product boxes — so
// re-parsing per call cost ~12s of main-thread time in production traces (the #1 INP offender).
// Cache the parsed jar on the CLIENT and re-parse only when `document.cookie` actually changes
// (cheap string compare). Uses cookies-next's own `getCookies()` so decoding is identical.
// CLIENT-ONLY: on the server we read fresh per request via cookies-next (next/headers) — a
// module-level cache on the server would leak one request's cookies into another's.
let _cookieRaw: string | null = null;
let _cookieJar: Partial<Record<string, string>> = {};

const readCachedCookie = (key: string): string | undefined => {
  if (typeof document === "undefined") {
    return getCookie(key) as string | undefined; // server: never cache (per-request)
  }
  const raw = document.cookie;
  if (raw !== _cookieRaw) {
    _cookieRaw = raw;
    _cookieJar = (getCookies() ?? {}) as Partial<Record<string, string>>;
  }
  return _cookieJar[key];
};

//============== branch id ================
export const storeBranchIdCookie = (branchId: number) => {
  storeCookei(CookieKeys.branch_id, branchId?.toString());
};
export const getBranchIdCookie = () =>
  (readCachedCookie(CookieKeys.branch_id) as string) ?? null;
export const deleteBranchIdCookie = () => removeCookie(CookieKeys.branch_id);
// ==================== Agent-id =======================

// The agent id is the ONLY thing tying an anonymous visitor to their cart — the
// backend stores it verbatim as `carts.session_key` — so losing it loses the cart.
//
// It is minted SERVER-SIDE by `src/proxy.ts` (see middleware-helpers/agent-id.ts)
// on the first navigation that arrives without the cookie. This module is the
// client reader, and it recovers rather than re-mints:
//
//   cookie  →  localStorage mirror (and restore the cookie from it)  →  mint
//
// The mirror exists because WebKit caps script-written cookies hard when a page
// is entered through a link-decorated cross-site navigation — every Instagram
// link — so on iOS in-app browsers the cookie could disappear mid-session. Each
// cookie-less request then minted a new identity, the backend created a new empty
// cart, and the shopper's cart appeared to empty itself.
//
// This also sits on the hot path: `apiConfig()` builds headers for every client
// API service, constructed inside hooks that run per render (~100 product boxes ×
// several queries each). The pre-memo implementation measured 769 writes per page
// view, all writing the identical value, and ~2.8s of `getCookies` CPU during a
// single scroll — because each write changes `document.cookie`, which invalidated
// `readCachedCookie`'s memo for EVERY other reader in this file too. So: read
// through the memo, and touch `document.cookie` at most once per page view.
// CLIENT-ONLY memo — see the note above `readCachedCookie`; a module-level cache
// on the server would leak one request's id into another's.

// Keep in step with AGENT_ID_MAX_AGE_DAYS in middleware-helpers/agent-id.ts.
const AGENT_ID_AGE_DAYS = 400;

let _agentIdMemo: string | undefined;
let _agentIdRefreshed = false;

const readAgentIdMirror = (): string | undefined => {
  try {
    return window.localStorage.getItem(CookieKeys.agent_id) || undefined;
  } catch {
    // Storage disabled / over quota (Safari private mode) — the cookie is primary.
    return undefined;
  }
};

const persistAgentId = (value: string) => {
  storeCookei(CookieKeys.agent_id, value, AGENT_ID_AGE_DAYS);
  try {
    window.localStorage.setItem(CookieKeys.agent_id, value);
  } catch {
    // Best-effort mirror.
  }
};

export const getAgentIdCookie = (): string => {
  // SERVER: never fabricate an identity. `ssrConfig()` already passes the real
  // cookie value whenever the request carries one, so reaching here means we
  // cannot identify this visitor — and returning "" makes `cleanObject` drop the
  // `Agent-Id` header entirely (see apiConfig) instead of attaching the request to
  // a throwaway cart. The previous `|| getRandom()` minted an unpersisted id per
  // cookie-less server request: one junk `init` cart per SSR render, and on the
  // cart page it rendered that brand-new empty cart over the shopper's real one.
  if (typeof document === "undefined") return "";

  if (_agentIdMemo) return _agentIdMemo;

  const fromMirror = readAgentIdMirror();
  const fromCookie = readCachedCookie(CookieKeys.agent_id);

  // The MIRROR WINS when the two disagree, and that ordering is load-bearing.
  // The proxy mints an id whenever a request arrives without the cookie — dropped,
  // expired, or capped by the browser — not because the visitor is new. So a cookie
  // that disagrees with an existing mirror is a fresh id for a device whose cart is
  // already under the mirrored one; adopting it would orphan that cart, which is the
  // exact failure we are fixing. Taking the mirror and writing it back to the cookie
  // (below) converges the server on the same id from the next request onwards.
  const agentId = fromMirror ?? fromCookie ?? getRandom();
  _agentIdMemo = agentId;

  // One write per page view — never per read; see CPU-2 above. That single write
  // slides the expiry, restores a cookie the browser dropped, and repairs a
  // cookie/mirror divergence, so it is worth doing unconditionally.
  if (!_agentIdRefreshed) {
    _agentIdRefreshed = true;
    persistAgentId(agentId);
  }
  return agentId;
};
//============== language ================
export const storeLangCookie = (lang: string) => {
  storeCookei(CookieKeys.language, lang);
};
export const getLangCookie = () => {
  const lang = readCachedCookie(CookieKeys.language) as "en" | "ar";
  return lang;
};

//============== currency ================

export const storeCurrencyCookie = (currencyCode: string) => {
  storeCookei(CookieKeys.currency_code, currencyCode);
};
export const getCurrencyIdCookie = () => {
  const currency = readCachedCookie(CookieKeys.currency_code);
  return currency;
};
export const deleteCurrencyCookie = () =>
  removeCookie(CookieKeys.currency_code);
//============== Tokens ================
export const storeAccessToken = (accessToken: string) => {
  storeCookei(CookieKeys.access_token, accessToken, 30);
};
export const storeAuthTokens = (accessToken: string, refreshToken: string) => {
  storeCookei(CookieKeys.access_token, accessToken, 30);
  storeCookei(CookieKeys.refresh_token, refreshToken, 30);
};
export const deleteTokens = () => {
  removeCookie(CookieKeys.access_token);
  removeCookie(CookieKeys.refresh_token);
};
export const getAccessToken = () => getCookie(CookieKeys.access_token);
export const getRefreshToken = () => getCookie(CookieKeys.refresh_token);
export const getFbpCookie = () => getCookie(CookieKeys.fbp);
export const getFbcCookie = () => getCookie(CookieKeys.fbc);
export const getTtpCookie = () => getCookie(CookieKeys.ttp);
export const getTtclidCookie = () => getCookie(CookieKeys.ttclid);
export const getFbLoginIdCookie = () => getCookie(CookieKeys.fb_login_id);
export const getTwclidCookie = () => getCookie(CookieKeys.twclid);
//====================================
// Read through the memo: this is the other raw reader on the `apiConfig()` hot path.
// NOTE: deliberately NOT applied to getAccessToken/getRefreshToken above — those must
// reflect a token refresh or logout immediately.
export const getIPCookie = () => readCachedCookie(CookieKeys.ip);

//====================================
export const getResetLastViewedCookie = () =>
  getCookie(CookieKeys.reset_last_viewed);
export const deleteResetLastViewedCookie = () =>
  removeCookie(CookieKeys.reset_last_viewed);
